import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAPI } from "../contexts/APIContext";
import { useAuth } from "../contexts/AuthContext";
import logger from "../utils/logger";

/**
 * Records `OPENGAME_<GAME_TYPE>` whenever a family lands on a game.
 *
 * Mounted once, inside the router. It watches the URL instead of wrapping
 * routes or editing game pages, so every way into a game is covered — the
 * games map, a deep link, a refresh, a back button — and adding a game later
 * only means adding a line to the map below.
 *
 * The tag carries the canonical UPPER_SNAKE game key that the games API and
 * `profiles.game_difficulty` already use, not the kebab-case route slug, so
 * the retention system does not have to translate.
 */
export const GAME_TYPE_BY_SLUG = {
  "bubble-pop": "BUBBLE_POP_MATH",
  "color-bubble-pop": "BUBBLE_POP",
  "story-puzzle": "STORY_PUZZLE",
  "animal-sound-farm": "ANIMAL_SOUND_FARM",
  "word-web-defender": "WORD_WEB_DEFENDER",
  "math-castle-defense": "MATH_CASTLE_DEFENSE",
  "feed-monster": "FEED_MONSTER",
  "critter-crossing": "CRITTER_CROSSING",
  "city-helpers": "CITY_HELPERS",
};

export function gameTypeForPath(pathname) {
  const match = /^\/games\/([^/?#]+)/.exec(pathname || "");
  return match ? GAME_TYPE_BY_SLUG[match[1]] || null : null;
}

export default function GameOpenTracker() {
  const location = useLocation();
  const { apiClient } = useAPI();
  const { user } = useAuth();
  // Which game we last tagged. Comparing the value rather than flipping a
  // boolean means a StrictMode double-invoke sends one tag, while leaving a
  // game and coming back to a different one still sends its own.
  const taggedGameType = useRef(null);

  const gameType = gameTypeForPath(location.pathname);

  useEffect(() => {
    if (!gameType) {
      taggedGameType.current = null;
      return;
    }
    if (!user?.id || !apiClient) return;
    if (taggedGameType.current === gameType) return;
    taggedGameType.current = gameType;
    const tag = `OPENGAME_${gameType}`;
    apiClient
      .addUserDataTag(user.id, tag)
      .catch((error) => logger.error(`❌ Error adding open game tag ${tag}:`, error));
  }, [gameType, user?.id, apiClient]);

  return null;
}
